import type { ReactNode } from 'react';

import { usePermission } from '@/hooks/usePermissions';
import { useSupabase } from '@/hooks/useSupabase';
import type { AppPermission } from '@/types/app-permissions';

interface Props {
  permission: AppPermission;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Render condicional según permiso.
 *
 * Uso:
 *   <RequirePermission permission="prospects.write.all">
 *     <Button>Crear prospect</Button>
 *   </RequirePermission>
 *
 * Para guards de routes enteras usar `RequireAuth` + `RequirePermission`
 * en el route element directamente.
 *
 * 🔴 Mientras la sesión carga NO se decide nada. Los permisos salen de los
 * claims del token: sin token todavía leído, la respuesta a «¿puede?» no es
 * «no», es «todavía no sé». Cuando esto devolvía el fallback durante la carga,
 * la pantalla que aparecía era «No tenés acceso a esta sección».
 */
export function RequirePermission({
  permission,
  children,
  fallback = null,
}: Props) {
  const { loading } = useSupabase();
  const allowed = usePermission(permission);
  if (loading) return null;
  return <>{allowed ? children : fallback}</>;
}
